/**
 * Fonte exportável: CRM / Negócios
 * -----------------------------------------------------------------------------
 * Declara todas as colunas exportáveis do negócio e busca as linhas respeitando
 * EXATAMENTE os filtros aplicados na tela (Kanban ou listagem "Todos os
 * Negócios"). Campos com relacionamento saem com o NOME (cliente, vendedor,
 * fase, funil, etiquetas, produtos), nunca com o identificador interno.
 *
 * @version 1.6.0
 * RELEASE: 2026-09-07 v1.6.0 - O ponto de continuação passa a ser o PAR
 *          (data, id) — p_cursor_updated_at + p_cursor_id. Sem o id, todos os
 *          registros gravados no mesmo instante (importações em lote) eram
 *          descartados e o arquivo saía menor que o total exibido na tela.
 * RELEASE: 2026-09-07 v1.5.0 - Kanban: coleta de ids passa a ser feita em BLOCOS
 *          de 500 usando o ponto de continuação (p_cursor_updated_at) devolvido
 *          pela RPC. Antes uma única chamada pedia 5000 linhas e a camada de API
 *          truncava silenciosamente em 1000 registros.

 * RELEASE: 2026-09-07 v1.4.0 - Kanban: coleta de ids passa a usar a RPC
 *          `get_deals_export_ids` (uma única chamada, sem cursor). A validação
 *          de permissão e o limite de uso ficam no banco (regra única) e a
 *          consulta do Kanban é reaproveitada sem duplicar filtros.
 * RELEASE: 2026-09-07 v1.3.0 - Kanban: contagem do modal passa a usar o
 *          totalizador de colunas (sem carregar registros) e a coleta de ids
 *          na confirmação passa a paginar em lotes de 100 pelo cursor de data,
 *          sem alterar a RPC get_kanban_column (evita impacto de performance).

 * RELEASE: 2026-09-07 v1.2.0 - Implementa describeParams: traduz filtros e
 *          ordenação da tela para linhas legíveis gravadas na auditoria
 *          (`_resumo`), resolvendo nomes de funil, fase, vendedor, etiquetas,
 *          origens e motivos de perda — nunca códigos internos.
 * RELEASE: 2026-09-07 v1.1.0 - Adiciona countRows (total de registros filtrados)
 *          para exibição no modal de exportação.
 * RELEASE: 2026-09-07 v1.0.0 - Criação inicial (corrige exportação que ignorava
 *          etiquetas, origens, motivos de perda e origem do cadastro).
 */

import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import type { DealFilters } from '@/hooks/useCrmDeals';
import type { ExportColumn, ExportParamSummary, ExportSource } from '../types';

const logger = createLogger('ExportCrmDeals');

/** Teto de segurança de registros por exportação */
const LIMITE_EXPORTACAO = 5000;
/** Tamanho do lote ao enriquecer as linhas */
const LOTE = 500;
/** Tamanho do bloco ao coletar ids do Kanban (abaixo do teto de 1000 da API) */
const LOTE_IDS = 500;
/** Tamanho da página ao varrer a listagem */
const PAGINA = 200;

/** Parâmetros da tela que originou a exportação */
export type CrmDealsExportParams =
  | {
      kind: 'kanban';
      pipelineId: string;
      phaseId: string;
      phaseName?: string;
      filters?: DealFilters;
      sortField?: string;
      sortDirection?: string;
    }
  | {
      kind: 'list';
      filters?: DealFilters;
      sortField?: string;
      sortDirection?: string;
    };

/** Colunas disponíveis para exportação de negócios */
export const CRM_DEALS_COLUMNS: ExportColumn[] = [
  // Identificação
  { key: 'titulo', label: 'Título', group: 'Identificação', defaultSelected: true },
  { key: 'empresa', label: 'Empresa', group: 'Identificação', defaultSelected: true },
  { key: 'descricao', label: 'Descrição', group: 'Identificação' },
  // Valores
  { key: 'valor', label: 'Valor', group: 'Valores', kind: 'currency', defaultSelected: true },
  { key: 'moeda', label: 'Moeda', group: 'Valores' },
  { key: 'total_itens', label: 'Total dos produtos', group: 'Valores', kind: 'currency' },
  { key: 'probabilidade', label: 'Probabilidade (%)', group: 'Valores', kind: 'number' },
  // Situação
  { key: 'status', label: 'Status', group: 'Situação', defaultSelected: true },
  { key: 'prioridade', label: 'Prioridade', group: 'Situação', defaultSelected: true },
  { key: 'fase', label: 'Fase', group: 'Situação', defaultSelected: true },
  { key: 'funil', label: 'Funil', group: 'Situação' },
  { key: 'workspace', label: 'Workspace', group: 'Situação' },
  { key: 'fase_ganho', label: 'Fase de ganho', group: 'Situação', kind: 'boolean' },
  { key: 'fase_perda', label: 'Fase de perda', group: 'Situação', kind: 'boolean' },
  // Contato
  { key: 'contato_nome', label: 'Contato', group: 'Contato', defaultSelected: true },
  { key: 'contato_telefone', label: 'Telefone do contato', group: 'Contato', defaultSelected: true },
  { key: 'contato_email', label: 'E-mail do contato', group: 'Contato' },
  // Cliente
  { key: 'cliente_nome', label: 'Cliente', group: 'Cliente' },
  { key: 'cliente_documento', label: 'CPF/CNPJ do cliente', group: 'Cliente' },
  { key: 'cliente_cidade', label: 'Cidade do cliente', group: 'Cliente' },
  { key: 'cliente_estado', label: 'Estado do cliente', group: 'Cliente' },
  // Equipe
  { key: 'vendedor', label: 'Vendedor', group: 'Equipe', defaultSelected: true },
  { key: 'responsavel', label: 'Responsável', group: 'Equipe', defaultSelected: true },
  { key: 'criado_por', label: 'Criado por', group: 'Equipe' },
  // Classificação
  { key: 'origem', label: 'Origem', group: 'Classificação' },
  { key: 'motivo_perda', label: 'Motivo da perda', group: 'Classificação' },
  { key: 'etiquetas', label: 'Etiquetas', group: 'Classificação' },
  { key: 'produtos', label: 'Produtos', group: 'Classificação' },
  { key: 'origem_cadastro', label: 'Origem do cadastro', group: 'Classificação' },
  { key: 'origem_cadastro_nome', label: 'Detalhe da origem', group: 'Classificação' },
  // Datas
  { key: 'previsao_fechamento', label: 'Previsão de fechamento', group: 'Datas', kind: 'date' },
  { key: 'data_conclusao', label: 'Data de conclusão', group: 'Datas', kind: 'date' },
  { key: 'criado_em', label: 'Criado em', group: 'Datas', kind: 'datetime', defaultSelected: true },
  { key: 'atualizado_em', label: 'Atualizado em', group: 'Datas', kind: 'datetime' },
  { key: 'fase_atualizada_em', label: 'Fase atualizada em', group: 'Datas', kind: 'datetime' },
];

/** Normaliza um array de filtros (vazio vira null para a RPC) */
function lista(valores?: string[] | null): string[] | null {
  return valores && valores.length > 0 ? valores : null;
}

/**
 * Monta os parâmetros do Kanban com TODOS os filtros da tela
 * (inclui etiquetas, origens e motivos de perda — antes ausentes na exportação).
 */
export function buildKanbanRpcParams(
  pipelineId: string,
  phaseId: string,
  tenantId: string,
  filters?: DealFilters,
  sortField = 'created_at',
  sortDirection = 'desc',
) {
  return {
    p_pipeline_id: pipelineId,
    p_phase_id: phaseId,
    p_limit: LIMITE_EXPORTACAO,
    p_cursor_updated_at: null,
    p_tenant_hint: tenantId,
    p_search: filters?.search || null,
    p_assigned_to: filters?.assigned_to || null,
    p_seller_id: filters?.seller_id || null,
    p_status: filters?.status || null,
    p_priority: filters?.priority || null,
    p_probability_min: filters?.probability_min ?? null,
    p_probability_max: filters?.probability_max ?? null,
    p_source: filters?.source || null,
    p_product_id: filters?.product_id || null,
    p_category_id: filters?.category_id || null,
    p_date_from: filters?.date_from || null,
    p_date_to: filters?.date_to || null,
    p_sort_field: sortField,
    p_sort_direction: sortDirection,
    p_created_from: filters?.created_from || null,
    p_created_to: filters?.created_to || null,
    p_won_date_from: filters?.won_date_from || null,
    p_won_date_to: filters?.won_date_to || null,
    p_lost_date_from: filters?.lost_date_from || null,
    p_lost_date_to: filters?.lost_date_to || null,
    p_phase_updated_from: filters?.phase_updated_from || null,
    p_phase_updated_to: filters?.phase_updated_to || null,
    p_source_ids: lista(filters?.source_ids),
    p_loss_reason_ids: lista(filters?.loss_reason_ids),
    p_tag_ids: lista(filters?.tag_ids),
  };
}

/** Monta os parâmetros da listagem paginada com todos os filtros da tela */
export function buildListRpcParams(
  pagina: number,
  tamanho: number,
  tenantId: string,
  filters?: DealFilters,
  sortField = 'created_at',
  sortDirection = 'desc',
) {
  return {
    p_page: pagina,
    p_page_size: tamanho,
    p_tenant_hint: tenantId,
    p_workspace_id: filters?.workspace_id || null,
    p_pipeline_id: filters?.pipeline_id || null,
    p_phase_id: filters?.phase_id || null,
    p_status: filters?.status || null,
    p_priority: filters?.priority || null,
    p_search: filters?.search || null,
    p_assigned_to: filters?.assigned_to || null,
    p_seller_id: filters?.seller_id || null,
    p_source: filters?.source || null,
    p_source_ids: lista(filters?.source_ids),
    p_probability_min: filters?.probability_min ?? null,
    p_probability_max: filters?.probability_max ?? null,
    p_date_from: filters?.date_from || null,
    p_date_to: filters?.date_to || null,
    p_sort_field: sortField,
    p_sort_direction: sortDirection,
    p_created_from: filters?.created_from || null,
    p_created_to: filters?.created_to || null,
    p_won_date_from: filters?.won_date_from || null,
    p_won_date_to: filters?.won_date_to || null,
    p_lost_date_from: filters?.lost_date_from || null,
    p_lost_date_to: filters?.lost_date_to || null,
    p_phase_updated_from: filters?.phase_updated_from || null,
    p_phase_updated_to: filters?.phase_updated_to || null,
    p_tag_ids: lista(filters?.tag_ids),
    p_expected_close_from: filters?.expected_close_from || null,
    p_expected_close_to: filters?.expected_close_to || null,
    p_creation_origin_types: lista(filters?.creation_origin_types as string[] | undefined),
    p_creation_origin_source_id: filters?.creation_origin_source_id || null,
    p_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo',
    p_loss_reason_ids: lista(filters?.loss_reason_ids),
  };
}

/**
 * Monta os parâmetros do TOTALIZADOR de colunas do Kanban.
 * Devolve apenas contadores por fase (sem carregar registros), por isso é o
 * que usamos enquanto o modal de exportação está apenas sendo configurado.
 */
function buildKanbanCountsParams(
  pipelineId: string,
  tenantId: string,
  filters?: DealFilters,
) {
  return {
    p_pipeline_id: pipelineId,
    p_tenant_hint: tenantId,
    p_search: filters?.search || null,
    p_assigned_to: filters?.assigned_to || null,
    p_seller_id: filters?.seller_id || null,
    p_status: filters?.status || null,
    p_priority: filters?.priority || null,
    p_probability_min: filters?.probability_min ?? null,
    p_probability_max: filters?.probability_max ?? null,
    p_source: filters?.source || null,
    p_product_id: filters?.product_id || null,
    p_category_id: filters?.category_id || null,
    p_date_from: filters?.date_from || null,
    p_date_to: filters?.date_to || null,
    p_created_from: filters?.created_from || null,
    p_created_to: filters?.created_to || null,
    p_won_date_from: filters?.won_date_from || null,
    p_won_date_to: filters?.won_date_to || null,
    p_lost_date_from: filters?.lost_date_from || null,
    p_lost_date_to: filters?.lost_date_to || null,
    p_phase_updated_from: filters?.phase_updated_from || null,
    p_phase_updated_to: filters?.phase_updated_to || null,
    p_source_ids: lista(filters?.source_ids),
    p_loss_reason_ids: lista(filters?.loss_reason_ids),
    p_tag_ids: lista(filters?.tag_ids),
  };
}

/**
 * Coleta os ids da coluna do Kanban para exportação.
 * v1.5.0: lê em BLOCOS de 500 usando o ponto de continuação devolvido pela RPC
 * (`cursor_at`), até esgotar a coluna ou alcançar o teto de exportação. Assim a
 * resposta nunca encosta no limite de 1000 linhas da camada de API.
 * A RPC continua validando a permissão (regra única), aplicando o limite de uso
 * e REAPROVEITANDO a consulta do Kanban (nenhum filtro é duplicado aqui).
 */
async function fetchKanbanIds(
  tenantId: string,
  params: Extract<CrmDealsExportParams, { kind: 'kanban' }>,
): Promise<string[]> {
  const base = buildKanbanRpcParams(
    params.pipelineId,
    params.phaseId,
    tenantId,
    params.filters,
    params.sortField,
    params.sortDirection,
  );
  const { p_cursor_updated_at: _ignorado, ...semCursor } = base;

  const ids: string[] = [];
  // v1.6.0: o ponto de continuação é o PAR (data, id) do último registro lido.
  // Sem o id, registros gravados no mesmo instante eram descartados.
  let cursor: string | null = null;
  let cursorId: string | null = null;

  for (;;) {
    const restante = LIMITE_EXPORTACAO - ids.length;
    const tamanho = Math.min(LOTE_IDS, restante);

    const { data, error } = await supabase.rpc('get_deals_export_ids', {
      ...semCursor,
      p_limit: tamanho,
      p_cursor_updated_at: cursor,
      p_cursor_id: cursorId,
    } as never);
    if (error) throw error;

    const linhas = (data || []) as Array<{ id: string; cursor_at: string }>;
    ids.push(...linhas.map((l) => l.id));

    // Fim quando o bloco veio incompleto ou o teto foi alcançado
    if (linhas.length < tamanho || ids.length >= LIMITE_EXPORTACAO) break;
    const ultima = linhas[linhas.length - 1];
    cursor = ultima?.cursor_at ?? null;
    cursorId = ultima?.id ?? null;
    if (!cursor || !cursorId) break;
  }


  logger.debug('Ids coletados no Kanban para exportação', { total: ids.length });
  return ids.slice(0, LIMITE_EXPORTACAO);
}


/** Busca os ids dos negócios exatamente como estão filtrados na tela */
async function fetchDealIds(tenantId: string, params: CrmDealsExportParams): Promise<string[]> {
  if (params.kind === 'kanban') {
    return fetchKanbanIds(tenantId, params);
  }

  // Listagem: varre as páginas até esgotar (ou atingir o teto)
  const ids: string[] = [];
  let pagina = 1;
  for (;;) {
    const { data, error } = await supabase.rpc(
      'get_deals_paginated',
      buildListRpcParams(
        pagina,
        PAGINA,
        tenantId,
        params.filters,
        params.sortField,
        params.sortDirection,
      ) as never,
    );
    if (error) throw error;
    const linhas = (data || []) as Array<{ id: string; total_count: number }>;
    ids.push(...linhas.map((l) => l.id));
    const total = Number(linhas[0]?.total_count ?? ids.length);
    if (linhas.length < PAGINA || ids.length >= total || ids.length >= LIMITE_EXPORTACAO) break;
    pagina += 1;
  }
  return ids.slice(0, LIMITE_EXPORTACAO);
}

/**
 * Conta quantos negócios seriam exportados com os filtros atuais.
 * Kanban: usa o TOTALIZADOR de colunas (só números, nenhum registro carregado).
 * Listagem: lê apenas o total_count da primeira página (page_size = 1).
 */
async function countDealRows(tenantId: string, params: CrmDealsExportParams): Promise<number> {
  if (params.kind === 'kanban') {
    const { data, error } = await supabase.rpc(
      'get_kanban_column_counts',
      buildKanbanCountsParams(params.pipelineId, tenantId, params.filters) as never,
    );
    if (error) throw error;
    const linhas = (data || []) as Array<{ phase_id: string; deal_count: number }>;
    const fase = linhas.find((l) => l.phase_id === params.phaseId);
    return Number(fase?.deal_count ?? 0);
  }

  const { data, error } = await supabase.rpc(
    'get_deals_paginated',
    buildListRpcParams(1, 1, tenantId, params.filters, params.sortField, params.sortDirection) as never,
  );
  if (error) throw error;
  const linhas = (data || []) as Array<{ total_count?: number }>;
  return Number(linhas[0]?.total_count ?? 0);
}


/** Enriquece os ids com todos os campos legíveis do negócio */
async function fetchDealRows(tenantId: string, ids: string[]) {
  const porId = new Map<string, Record<string, unknown>>();

  for (let i = 0; i < ids.length; i += LOTE) {
    const lote = ids.slice(i, i + LOTE);
    const { data, error } = await supabase.rpc('get_deals_export_rows', {
      p_deal_ids: lote,
      p_tenant_hint: tenantId,
    } as never);
    if (error) throw error;
    for (const linha of (data || []) as Array<{ deal_id: string; data: Record<string, unknown> }>) {
      porId.set(linha.deal_id, linha.data);
    }
  }

  // Preserva a ordem exibida na tela
  return ids.map((id) => porId.get(id)).filter(Boolean) as Record<string, unknown>[];
}

// ============================================================================
// describeParams — traduz os parâmetros da tela em linhas legíveis p/ auditoria
// ============================================================================

/** Rótulos das situações do negócio (mesmos exibidos nos filtros da tela) */
const ROTULO_STATUS: Record<string, string> = {
  open: 'Em andamento',
  won: 'Ganho',
  lost: 'Perdido',
};

/** Rótulos das prioridades */
const ROTULO_PRIORIDADE: Record<string, string> = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
  urgent: 'Urgente',
};

/** Rótulos da origem do cadastro */
const ROTULO_ORIGEM_CADASTRO: Record<string, string> = {
  manual: 'Cadastro manual',
  import: 'Importação',
  webhook: 'Webhook',
  scheduling: 'Agendamento',
  flow: 'Automação (Flow)',
  api: 'API',
};

/** Rótulos dos campos de ordenação da listagem */
const ROTULO_ORDENACAO: Record<string, string> = {
  created_at: 'Data de criação',
  updated_at: 'Última atualização',
  title: 'Título',
  value: 'Valor',
  expected_close_date: 'Previsão de fechamento',
  actual_close_date: 'Data de conclusão',
};

/** Formata uma data ISO curta para dd/mm/aaaa (fallback: texto original) */
function dataCurta(valor?: string | null): string {
  if (!valor) return '';
  const d = new Date(valor);
  if (Number.isNaN(d.getTime())) return valor;
  return d.toLocaleDateString('pt-BR');
}

/** Monta o texto de um intervalo a partir das pontas informadas */
function intervalo(de?: string | null, ate?: string | null): string | null {
  const ini = dataCurta(de);
  const fim = dataCurta(ate);
  if (ini && fim) return `${ini} até ${fim}`;
  if (ini) return `a partir de ${ini}`;
  if (fim) return `até ${fim}`;
  return null;
}

/** Consulta auxiliar: devolve mapa id -> nome de uma tabela de apoio */
async function mapaNomes(
  tabela: string,
  coluna: string,
  ids: string[],
): Promise<Map<string, string>> {
  const unicos = Array.from(new Set(ids.filter(Boolean)));
  const mapa = new Map<string, string>();
  if (unicos.length === 0) return mapa;
  const { data, error } = await supabase
    .from(tabela as never)
    .select(`id, ${coluna}`)
    .in('id', unicos);
  if (error) {
    // Falha na tradução não impede a exportação — fica o id abreviado
    logger.warn(`Falha ao resolver nomes de ${tabela}:`, error.message);
    return mapa;
  }
  for (const linha of (data || []) as Array<Record<string, unknown>>) {
    mapa.set(String(linha.id), String(linha[coluna] ?? ''));
  }
  return mapa;
}

/** Abrevia um identificador interno quando o nome não foi encontrado */
function idAbreviado(id: string | null | undefined): string {
  return (id || '').slice(0, 8).toUpperCase();
}

/**
 * Traduz os parâmetros da exportação de Negócios para linhas legíveis.
 * Falhas de tradução nunca quebram a exportação: devolve o que conseguiu.
 */
async function describeDealParams(
  _tenantId: string,
  params: CrmDealsExportParams,
): Promise<ExportParamSummary[]> {
  const resumo: ExportParamSummary[] = [];
  const f = params.filters || {};

  const filtro = (label: string, value: string | null | undefined) => {
    if (value) resumo.push({ group: 'filtro', label, value });
  };

  // De onde veio a exportação
  if (params.kind === 'kanban') {
    filtro('Origem', `Coluna do Kanban${params.phaseName ? ` — ${params.phaseName}` : ''}`);
  } else {
    filtro('Origem', 'Listagem "Todos os Negócios"');
  }

  // Filtros simples (sem consulta)
  filtro('Busca', f.search?.trim() ? `"${f.search.trim()}"` : null);
  filtro('Situação', f.status ? ROTULO_STATUS[f.status] ?? f.status : null);
  filtro('Prioridade', f.priority ? ROTULO_PRIORIDADE[f.priority] ?? f.priority : null);
  if (f.probability_min != null || f.probability_max != null) {
    const faixa =
      f.probability_min != null && f.probability_max != null
        ? `${f.probability_min}% a ${f.probability_max}%`
        : f.probability_min != null
          ? `a partir de ${f.probability_min}%`
          : `até ${f.probability_max}%`;
    filtro('Probabilidade', faixa);
  }
  filtro('Criados em', intervalo(f.created_from, f.created_to));
  filtro('Ganhos em', intervalo(f.won_date_from, f.won_date_to));
  filtro('Perdidos em', intervalo(f.lost_date_from, f.lost_date_to));
  filtro('Fase atualizada em', intervalo(f.phase_updated_from, f.phase_updated_to));
  filtro('Previsão de fechamento', intervalo(f.expected_close_from, f.expected_close_to));
  filtro('Data de conclusão', intervalo(f.date_from, f.date_to));
  if (f.creation_origin_types?.length) {
    filtro(
      'Origem do cadastro',
      f.creation_origin_types.map((t) => ROTULO_ORIGEM_CADASTRO[t] ?? t).join(', '),
    );
  }
  if (f.source && !f.source_ids?.length) {
    // Origem pode ser texto livre (ex.: "indicação") ou identificador
    const pareceId = /^[0-9a-f-]{32,36}$/i.test(f.source);
    filtro('Origem', pareceId ? idAbreviado(f.source) : f.source);
  }

  // Filtros com relacionamento (resolve nomes em consultas pontuais)
  const consultas: Promise<void>[] = [];

  if (params.kind === 'kanban' && !params.phaseName) {
    consultas.push(
      mapaNomes('crm_pipeline_phases', 'name', [params.phaseId]).then((m) =>
        filtro('Fase', m.get(params.phaseId) ?? idAbreviado(params.phaseId)),
      ),
    );
    consultas.push(
      mapaNomes('crm_pipelines', 'name', [params.pipelineId]).then((m) =>
        filtro('Funil', m.get(params.pipelineId) ?? idAbreviado(params.pipelineId)),
      ),
    );
  }
  if (f.pipeline_id && params.kind !== 'kanban') {
    consultas.push(
      mapaNomes('crm_pipelines', 'name', [f.pipeline_id]).then((m) =>
        filtro('Funil', m.get(f.pipeline_id!) ?? idAbreviado(f.pipeline_id)),
      ),
    );
  }
  if (f.phase_id && params.kind !== 'kanban') {
    consultas.push(
      mapaNomes('crm_pipeline_phases', 'name', [f.phase_id]).then((m) =>
        filtro('Fase', m.get(f.phase_id!) ?? idAbreviado(f.phase_id)),
      ),
    );
  }
  if (f.workspace_id) {
    consultas.push(
      mapaNomes('crm_workspaces', 'name', [f.workspace_id]).then((m) =>
        filtro('Workspace', m.get(f.workspace_id!) ?? idAbreviado(f.workspace_id)),
      ),
    );
  }
  if (f.seller_id) {
    consultas.push(
      mapaNomes('crm_sellers', 'name', [f.seller_id]).then((m) =>
        filtro('Vendedor', m.get(f.seller_id!) ?? idAbreviado(f.seller_id)),
      ),
    );
  }
  if (f.assigned_to) {
    consultas.push(
      mapaNomes('profiles', 'full_name', [f.assigned_to]).then((m) =>
        filtro('Responsável', m.get(f.assigned_to!) ?? idAbreviado(f.assigned_to)),
      ),
    );
  }
  if (f.source_ids?.length) {
    consultas.push(
      mapaNomes('crm_lead_sources', 'label', f.source_ids).then((m) =>
        filtro(
          'Origens',
          f.source_ids!.map((id) => m.get(id) ?? idAbreviado(id)).join(', '),
        ),
      ),
    );
  }
  if (f.tag_ids?.length) {
    consultas.push(
      mapaNomes('crm_deal_tags', 'name', f.tag_ids).then((m) =>
        filtro('Etiquetas', f.tag_ids!.map((id) => m.get(id) ?? idAbreviado(id)).join(', ')),
      ),
    );
  }
  if (f.loss_reason_ids?.length) {
    consultas.push(
      mapaNomes('crm_loss_reasons', 'name', f.loss_reason_ids).then((m) =>
        filtro(
          'Motivos de perda',
          f.loss_reason_ids!.map((id) => m.get(id) ?? idAbreviado(id)).join(', '),
        ),
      ),
    );
  }
  if (f.product_id) {
    consultas.push(
      mapaNomes('crm_products', 'name', [f.product_id]).then((m) =>
        filtro('Produto', m.get(f.product_id!) ?? idAbreviado(f.product_id)),
      ),
    );
  }
  if (f.category_id) {
    consultas.push(
      mapaNomes('crm_product_categories', 'name', [f.category_id]).then((m) =>
        filtro('Categoria de produto', m.get(f.category_id!) ?? idAbreviado(f.category_id)),
      ),
    );
  }

  await Promise.all(consultas);

  // Ordenação (quando informada)
  if (params.sortField) {
    const campo = ROTULO_ORDENACAO[params.sortField] ?? params.sortField;
    const sentido =
      (params.sortDirection || 'desc') === 'desc'
        ? 'mais recentes primeiro'
        : 'mais antigos primeiro';
    resumo.push({ group: 'ordenacao', label: 'Ordenado por', value: `${campo} — ${sentido}` });
  }

  return resumo;
}

/** Fonte exportável de Negócios */
export const crmDealsExportSource: ExportSource<CrmDealsExportParams> = {
  id: 'crm_deals',
  label: 'Negócios',
  title: 'CRM — Negócios',
  moduleKey: 'crm',
  fileBaseName: 'negocios',
  columns: CRM_DEALS_COLUMNS,
  fetchRows: async (tenantId, params) => {
    const ids = await fetchDealIds(tenantId, params);
    logger.debug(`Exportação de negócios: ${ids.length} registro(s) filtrado(s)`);
    if (ids.length === 0) return [];
    return fetchDealRows(tenantId, ids);
  },
  countRows: countDealRows,
  // v1.2.0: traduz filtros/ordenação para linhas legíveis na auditoria
  describeParams: describeDealParams,
};
